import Button from 'components/button';
import Tooltip from 'components/crm/ui/Tooltip';
import { dayLabel, hourLabel } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import useText from 'hooks/useText';
import { ChevronDown, Maximize2, Minimize2, Minus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import RecipientField from './RecipientField';
import SchedulePopover from './SchedulePopover';
import { isMail } from './scheduleSlots';

const HeaderButton = ({ icon: Icon, label, onClick }) => (
  <Tooltip label={label}>
    <button
      type="button"
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="flex h-7 w-7 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
    >
      <Icon size={16} strokeWidth={2} />
    </button>
  </Tooltip>
);

// The addresses of the record: its own (a company's general one,
// `recordEmail`), then its contacts', each once.
const optionsOf = (data, recordTag) => {
  const own = data.recordEmail ? [{ name: data.recordName ?? '', email: data.recordEmail, tag: recordTag }] : [];
  const seen = new Set();
  return [...own, ...(data.contacts ?? [])].filter((one) => {
    const address = one.email?.toLowerCase();
    if (!address || seen.has(address)) return false;
    seen.add(address);
    return true;
  });
};

// The recipient the window opens with: the contact it was opened for, else
// the record's own address, else its first contact with one.
const firstRecipients = (data, options) => {
  const contact =
    (data.contactName && options.find((one) => !one.tag && one.name === data.contactName)) || options[0];
  return contact ? [{ name: contact.name, address: contact.email }] : [];
};

// { address: where it is } of the lines other than `key`, so that an address
// sits in one line only.
const elsewhereOf = (lines, key, labels) =>
  Object.fromEntries(
    Object.entries(lines)
      .filter(([other, list]) => other !== key && list)
      .flatMap(([other, list]) => list.map((one) => [one.address.toLowerCase(), labels[other]])),
  );

// What is typed and valid joins the chips when the e-mail leaves.
const withTyped = (recipients, typed) =>
  isMail(typed) && !recipients.some((one) => one.address.toLowerCase() === typed.trim().toLowerCase())
    ? [...recipients, { name: '', address: typed.trim() }]
    : recipients;

/**
 * The window that writes an e-mail from a record (MailComposerProvider): at
 * the bottom right as in Gmail, it shrinks to its title bar or grows to the
 * middle of the screen. « Envoyer » sends at once; its arrow programs the
 * sending. The e-mail joins the record's activity (crmApi.sendRecordEmail).
 */
const MailComposer = ({ data, onClose }) => {
  const { text } = useText('mailComposer');
  const { call } = useCrmContext();
  const notify = useNotifyRecordChanged();
  const [size, setSize] = useState('normal');
  const [options] = useState(() => optionsOf(data, text('recordTag')));
  const [to, setTo] = useState(() => firstRecipients(data, options));
  const [cc, setCc] = useState(null);
  const [bcc, setBcc] = useState(null);
  const [typed, setTyped] = useState({ to: '', cc: '', bcc: '' });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [busy, setBusy] = useState(false);
  const target = data.groupId ? { groupId: data.groupId } : { companyId: data.companyId };
  const lines = { to, cc, bcc };
  const where = { to: text('inTo'), cc: text('inCc'), bcc: text('inBcc') };
  const pickerTexts = { open: (label) => text('pickFor', { label }), search: text('pickSearch'), empty: text('pickEmpty') };
  const lineProps = (key) => ({ contacts: options, elsewhere: elsewhereOf(lines, key, where), pickerTexts, ...typedFor(key) });
  const typedFor = (key) => ({ text: typed[key], onText: (value) => setTyped((current) => ({ ...current, [key]: value })) });

  const allTo = withTyped(to, typed.to);
  // A typed text that is no address keeps the e-mail from leaving.
  const badTyped = Object.values(typed).some((value) => value.trim() && !isMail(value));
  const ready = allTo.length > 0 && !badTyped && Boolean(subject.trim() || body.trim()) && !busy;

  const send = async (scheduledAt = null) => {
    if (!ready) return;
    setScheduling(false);
    setBusy(true);
    const named = (list) => list.map((one) => one.address);
    try {
      await call(crmApi.sendRecordEmail, {
        ...target,
        contactName: allTo[0].name,
        address: allTo[0].address,
        to: named(allTo),
        cc: named(withTyped(cc ?? [], typed.cc)),
        bcc: named(withTyped(bcc ?? [], typed.bcc)),
        subject,
        body,
        scheduledAt: scheduledAt?.toISOString() ?? null,
      });
      toast.success(scheduledAt ? text('scheduledToast', { day: dayLabel(scheduledAt).toLowerCase(), time: hourLabel(scheduledAt) }) : text('sentToast'));
      notify();
      onClose();
    } catch (error) {
      toast.error(error.message);
      setBusy(false);
    }
  };

  const title = subject.trim() || text('newMessage');
  const header = (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setSize((current) => (current === 'minimized' ? 'normal' : 'minimized'))}
      onKeyDown={(event) => event.key === 'Enter' && event.target === event.currentTarget && setSize(size === 'minimized' ? 'normal' : 'minimized')}
      className={`flex cursor-pointer items-center gap-2 bg-gray-50 py-2 pl-4 pr-2 ${size === 'minimized' ? 'rounded-t-xl' : 'rounded-t-xl border-b border-gray-100'}`}
    >
      <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{title}</p>
      <HeaderButton
        icon={Minus}
        label={text(size === 'minimized' ? 'restore' : 'minimize')}
        onClick={() => setSize(size === 'minimized' ? 'normal' : 'minimized')}
      />
      <HeaderButton
        icon={size === 'expanded' ? Minimize2 : Maximize2}
        label={text(size === 'expanded' ? 'shrink' : 'expand')}
        onClick={() => setSize(size === 'expanded' ? 'normal' : 'expanded')}
      />
      <HeaderButton icon={X} label={text('close')} onClick={onClose} />
    </div>
  );

  const toggles = (
    <div className="flex shrink-0 items-center gap-2 text-sm text-gray-500">
      {cc === null && (
        <button type="button" onClick={() => setCc([])} className="hover:text-gray-900 hover:underline">
          {text('cc')}
        </button>
      )}
      {bcc === null && (
        <button type="button" onClick={() => setBcc([])} className="hover:text-gray-900 hover:underline">
          {text('bcc')}
        </button>
      )}
    </div>
  );

  const WINDOW_CLASSES = {
    normal: 'fixed bottom-0 right-6 z-50 h-[min(560px,calc(100vh-80px))] w-[min(560px,calc(100vw-32px))] rounded-t-xl',
    minimized: 'fixed bottom-0 right-6 z-50 w-80 rounded-t-xl',
    expanded: 'relative h-[calc(100vh-96px)] w-[min(960px,calc(100vw-48px))] rounded-xl',
  };

  const composer = (
    <section
      aria-label={text('newMessage')}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) send();
      }}
      className={`flex flex-col border border-gray-200 bg-white shadow-pop motion-safe:animate-slideUpIn ${WINDOW_CLASSES[size]}`}
    >
      {header}
      {size !== 'minimized' && (
        <>
          <RecipientField label={text('to')} recipients={to} onChange={setTo} autoFocus={!to.length} {...lineProps('to')}>
            {toggles}
          </RecipientField>
          {cc !== null && <RecipientField label={text('cc')} recipients={cc} onChange={setCc} autoFocus {...lineProps('cc')} />}
          {bcc !== null && <RecipientField label={text('bcc')} recipients={bcc} onChange={setBcc} autoFocus {...lineProps('bcc')} />}
          <input
            type="text"
            aria-label={text('subject')}
            placeholder={text('subject')}
            autoFocus={to.length > 0}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            className="border-0 border-b border-gray-100 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:border-gray-100 focus:outline-none focus:ring-0"
          />
          <textarea
            aria-label={text('message')}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            className="min-h-0 flex-1 resize-none border-0 px-4 py-3 text-sm leading-[21px] text-gray-900 focus:outline-none focus:ring-0"
          />
          <div className="relative flex items-center gap-2 px-4 py-3">
            <div className="flex">
              <Button color="PRIMARY" size="SM" className="rounded-r-none" disabled={!ready} onClick={() => send()}>
                {text('send')}
              </Button>
              <Button
                color="PRIMARY"
                size="SMI"
                className="rounded-l-none border-l border-yellow-600"
                aria-label={text('scheduleTitle')}
                aria-expanded={scheduling}
                disabled={!ready}
                onClick={() => setScheduling((current) => !current)}
              >
                <ChevronDown size={18} strokeWidth={2} />
              </Button>
            </div>
            <p className="min-w-0 flex-1 truncate text-xs text-gray-500">{text('demoHint')}</p>
            <HeaderButton icon={Trash2} label={text('discard')} onClick={onClose} />
            {scheduling && <SchedulePopover onPick={send} onClose={() => setScheduling(false)} />}
          </div>
        </>
      )}
    </section>
  );

  return createPortal(
    size === 'expanded' ? (
      // The veil gives the window back its size, as Gmail's does.
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40" onMouseDown={() => setSize('normal')}>
        <div onMouseDown={(event) => event.stopPropagation()}>{composer}</div>
      </div>
    ) : (
      composer
    ),
    document.body,
  );
};

export default MailComposer;
